import '@mdi/font/css/materialdesignicons.min.css';
import './scss/main.scss';

import axios from 'axios';
import Vue from 'vue';
import Vuex, { Store as VuexStore } from 'vuex';
import { AppVue } from '@/primary/app';
import { isMobile } from '@/primary/responsiveUtils';
import { StaticFileCountryRepository } from '@/secondary/country/StaticFileCountryRepository';
import { RestProjectRepository } from '@/secondary/project/RestProjectRepository';
import { ConsoleLogger } from '@/secondary/ConsoleLogger';
import { Store } from '@/primary/store/Store';
import { State, storeOptions } from '@/primary/store/storeOptions';
import { createCountryVectorLayer } from '@/primary/webmapping/country/vector-layer/createCountryVectorLayer';
import { createCountryMap } from '@/primary/webmapping/country/map/createCountryMap';
import { createCountryView } from '@/primary/webmapping/country/view/createCountryView';
import { OLMap } from '@/primary/webmapping/common/OLMap';
import { OLLayer } from '@/primary/webmapping/common/OLLayer';
import { GeoJSON } from 'ol/format';
import { MittClickBus } from '@/primary/MittClickBus';
import mitt from 'mitt';
import { CountryRendering } from '@/primary/country/CountryRendering';
import { createCountryFeature, updateCountryFeature } from '@/primary/webmapping/country/feature/CountryFeature';
import { createCountryClickSelect } from '@/primary/webmapping/country/interaction/createCountryClickSelect';
import { OLClickSelect } from '@/primary/webmapping/common/OLClickSelect';
import { createCountrySelectionVectorLayer } from '@/primary/webmapping/country/vector-layer/createCountrySelectionVectorLayer';
import { Feature } from 'ol';
import { FilterFunction } from 'ol/interaction/Select';
import { StaticFileCountryLabelRepository } from '@/secondary/country/StaticFIleCountryLabelRepository';
import { createCountryLabelVectorLayer } from '@/primary/webmapping/country/vector-layer/createCountryLabelVectorLayer';
import { CountryLabel } from '@/domain/country/CountryLabel';
import { createCountryLabelFeature, updateCountryLabelFeature } from '@/primary/webmapping/country/feature/CountryLabelFeature';
import { RestResearchUnitRepository } from '@/secondary/research-unit/RestResearchUnitRepository';
import { InMemoryCache } from '@/secondary/InMemoryCache';

Vue.use(Vuex);

Vue.config.productionTip = false;

const clickMitt = mitt();
const clickBus = new MittClickBus(clickMitt);
const windowClick = (event: MouseEvent): void => clickBus.click(event);
window.onclick = windowClick;

const logger = new ConsoleLogger(console); // eslint-disable-line no-console
const store = new Store(new VuexStore<State>(storeOptions));
const staticFileAxios = axios.create({ baseURL: './data/' });
const staticFileCountryRepository = new StaticFileCountryRepository(staticFileAxios);
const staticFileCountryLabelRepository = new StaticFileCountryLabelRepository(staticFileAxios, store);
const mnhnAPIsAxios = axios.create({ baseURL: process.env.VUE_APP_MNHN_API_URL });
const restProjectRepository = new RestProjectRepository(mnhnAPIsAxios);
const restResearchUnitRepository = new RestResearchUnitRepository(mnhnAPIsAxios, new InMemoryCache());

const filterFunction = (feature: Feature) => feature.get('selectable');
const map = createCountryMap();
const view = createCountryView();
const countryLayer = createCountryVectorLayer();
const countryLabelLayer = createCountryLabelVectorLayer();
const countrySelectionLayer = createCountrySelectionVectorLayer();

const countryClickSelect = createCountryClickSelect({
  layers: [countryLayer],
  filter: filterFunction as FilterFunction,
  style: null as any,
});

map.setView(view);
map.addLayer(countryLayer);
map.addLayer(countrySelectionLayer);
map.addLayer(countryLabelLayer);
map.addInteraction(countryClickSelect);

const countryOLMap = new OLMap(map);
const countryOLLayer = new OLLayer<GeoJSON.MultiPolygon, CountryRendering>(countryLayer, createCountryFeature, updateCountryFeature);
const countryOLSelect = new OLClickSelect(countryClickSelect, countryLayer, countrySelectionLayer, filterFunction);

const countryLabelOLLayer = new OLLayer<GeoJSON.Point, CountryLabel>(
  countryLabelLayer,
  createCountryLabelFeature,
  updateCountryLabelFeature
);

new Vue({
  render: h => h(AppVue),
  provide: {
    clickBus: () => clickBus,
    logger: () => logger,
    store: () => store,
    countryOLMap: () => countryOLMap,
    countryOLLayer: () => countryOLLayer,
    countryOLSelect: () => countryOLSelect,
    countryLabelOLLayer: () => countryLabelOLLayer,
    countryRepository: () => staticFileCountryRepository,
    countryLabelRepository: () => staticFileCountryLabelRepository,
    projectRepository: () => restProjectRepository,
    researchUnitRepository: () => restResearchUnitRepository,
    globalWindow: () => window,
  },
}).$mount('#app');
